import { FormConfig } from 'components/common/form/FormConfig';
import { useFormikContext } from 'formik';
import { BasicOutdoorCreativeProperties } from 'core/creative/OutdoorCreative';
import { validateEmpty } from 'utils/ValidateUtils';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { BeaconType } from 'core/advertiser/BeaconConnection';
import styles from './outdoorFields.module.scss';
import i18n from 'i18n';
import cx from 'classnames/bind';
import { FormikCustomRenderProps } from 'components/common/form/field/CustomField';
import { camelCase, get } from 'lodash';
import { LineMessageType, LineUserLanguage } from 'core/advertiser/LineBeacon';
import { useTenMaxBeaconMessageFields } from './useTenMaxBeaconMessageFields';
import { TenMaxBeaconMessageType } from 'core/advertiser/TenmaxBeacon';
import { OutdoorFormModel } from '../OutdoorFormModel';
import { useLineMessageFields } from 'hooks/LineMessage';

const classNames = cx.bind(styles);

export const useOutdoorBeaconFields = <T extends OutdoorFormModel<T>>(
  model: T
) => {
  const {
    advertiserId,
    canChangeBeacon: modelCanChangeBeacon,
    beaconConnectionOptions,
    initBeaconSwitch
  } = model;
  const { values, setFieldValue } = useFormikContext<{
    typeProperties: BasicOutdoorCreativeProperties & {
      intervalHours: number;
      maxFrequency: number;
      beaconConnection?: SelectOptions;
    };
  }>();

  const {
    intervalHours,
    maxFrequency,
    beaconConnection: selectBeaconConnection
  } = values.typeProperties;

  const [beaconType, setBeaconType] = useState<BeaconType>(
    selectBeaconConnection
      ? selectBeaconConnection.extra
      : BeaconType.LINE_SIMPLE_BEACON
  );

  const beaconLabel = selectBeaconConnection
    ? selectBeaconConnection.label
    : '';

  const lineMessageFields = useLineMessageFields(beaconType, advertiserId);
  const tenMaxBeaconMessageFields = useTenMaxBeaconMessageFields(beaconType);

  const beaconConnectionOptionsOfType = useMemo(() => {
    if (!beaconConnectionOptions) {
      return [];
    }
    return beaconConnectionOptions.filter((beaconConnection) => {
      return beaconConnection.extra === beaconType;
    });
  }, [beaconConnectionOptions, beaconType]);

  const beaconConnection = useMemo(
    () =>
      selectBeaconConnection && beaconConnectionOptions
        ? beaconConnectionOptions.find(
            (beaconConnection) =>
              beaconConnection.value.toString() ===
              selectBeaconConnection.value.toString()
          )
        : undefined,
    [beaconConnectionOptions, selectBeaconConnection]
  );

  const beaconTypeOptions = useMemo(() => model.beaconTypeOptions, [model]);

  const [showBeaconConfig, setShowBeaconConfig] = useState<boolean>(
    beaconConnection !== undefined
  );
  const [showFrequencyCapSetting, setShowFrequencyCapSetting] =
    useState<boolean>(intervalHours > 0 && maxFrequency > 0);

  const validate = useCallback(
    (value: string) => {
      if (!showBeaconConfig) {
        return;
      }
      return validateEmpty(value);
    },
    [showBeaconConfig]
  );

  const initBeaconMessageFields = useCallback(
    (beaconType?: BeaconType) => {
      switch (beaconType) {
        case BeaconType.LINE_SIMPLE_BEACON:
          setFieldValue('lineMessage', [
            {
              type: LineMessageType.TEXT
            }
          ]);
          setFieldValue('typeProperties.language', LineUserLanguage.ALL);
          setFieldValue('tenMaxBeaconMessage', undefined);
          break;
        case BeaconType.TENMAX_BEACON:
          setFieldValue('tenMaxBeaconMessage', {
            type: TenMaxBeaconMessageType.TEXT,
            bundleIds: []
          });
          setFieldValue('lineMessage', undefined);
          setFieldValue('typeProperties.language', undefined);
          break;
        default:
          setFieldValue('lineMessage', undefined);
          setFieldValue('tenMaxBeaconMessage', undefined);
          setFieldValue('typeProperties.language', undefined);
          break;
      }
    },
    [setFieldValue]
  );

  const [canChangeBeacon, setCanChangeBeacon] =
    useState<boolean>(modelCanChangeBeacon);

  const onBeaconConfigSwitchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked;
      setShowBeaconConfig(value);
      setCanChangeBeacon(true);
      if (!value) {
        setFieldValue('typeProperties.beaconConnection', undefined);
        setFieldValue('typeProperties.intervalHours', undefined);
        setFieldValue('typeProperties.maxFrequency', undefined);
        setFieldValue('typeProperties.beaconType', undefined);
        initBeaconMessageFields();
      } else {
        setFieldValue('typeProperties.intervalHours', 0);
        setFieldValue('typeProperties.maxFrequency', 0);
        setFieldValue(
          'typeProperties.beaconType',
          BeaconType.LINE_SIMPLE_BEACON
        );
        initBeaconMessageFields(BeaconType.LINE_SIMPLE_BEACON);
      }
    },
    [setFieldValue, initBeaconMessageFields]
  );

  const onFrequencyCapSwitchChange = useCallback(
    (event) => {
      const value = event.target.checked;
      setShowFrequencyCapSetting(value);
      if (!value) {
        setFieldValue('typeProperties.intervalHours', 0);
        setFieldValue('typeProperties.maxFrequency', 0);
      } else {
        setFieldValue('typeProperties.intervalHours', 5);
        setFieldValue('typeProperties.maxFrequency', 1);
      }
    },
    [setFieldValue]
  );

  const handleInitBeaconSwitch = useCallback(() => {
    if (values.typeProperties.beaconConnection || !canChangeBeacon) {
      return;
    }
    const event: React.ChangeEvent<HTMLInputElement> = {
      target: { checked: initBeaconSwitch }
    } as React.ChangeEvent<HTMLInputElement>;
    onBeaconConfigSwitchChange(event);
  }, [
    initBeaconSwitch,
    values.typeProperties.beaconConnection,
    onBeaconConfigSwitchChange,
    canChangeBeacon
  ]);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;
    handleInitBeaconSwitch();
  }, [handleInitBeaconSwitch]);

  const renderFrequencyControlInput = useCallback(
    (props: FormikCustomRenderProps) => {
      const onMaxFrequencyChange = (e) => {
        props.setFieldValue(
          'typeProperties.maxFrequency',
          e.target.value < 0 ? 0 : e.target.value
        );
      };
      const onIntervalHoursChange = (e) => {
        props.setFieldValue(
          'typeProperties.intervalHours',
          e.target.value < 0 ? 0 : e.target.value
        );
      };
      const errorMaxFrequency = get(
        props.errors,
        'typeProperties.maxFrequency'
      );
      const errorintervalHours = get(
        props.errors,
        'typeProperties.intervalHours'
      );
      const maxFrequencyClass = classNames('maxFrequency', {
        error: errorMaxFrequency
      });
      const intervalHoursClass = classNames('intervalHours', {
        error: errorintervalHours
      });
      const timeUnitKey = +intervalHours > 1 ? 'hours' : 'hour';
      const cappingCountKey =
        +maxFrequency > 1 ? 'cappingCounts' : 'cappingCount';
      return (
        <div className={styles.frequencyControl}>
          <div className={styles.intervalHoursContainer}>
            {i18n.t('beaconFields.labels.frequencyCap.every') || ''}
            <input
              className={intervalHoursClass}
              name='frequency.intervalHours'
              value={intervalHours}
              onChange={onIntervalHoursChange}
              min={1}
              type='number'
            />
            {i18n.t(`common.units.${timeUnitKey}`)}
          </div>
          <div className={styles.maxFrequencyClassContainer}>
            {i18n.t('beaconFields.labels.frequencyCap.send')}
            <input
              className={maxFrequencyClass}
              value={maxFrequency}
              name='typeProperties.maxFrequency'
              onChange={onMaxFrequencyChange}
              min={1}
              type='number'
            />
            {i18n.t(`beaconFields.labels.frequencyCap.${cappingCountKey}`)}
          </div>
          {errorMaxFrequency && (
            <div className='errorTip'>{errorMaxFrequency as string}</div>
          )}
          {errorintervalHours && (
            <div className='errorTip'>{errorintervalHours as string}</div>
          )}
        </div>
      );
    },
    [intervalHours, maxFrequency]
  );

  const onBeaconTypeChange = useCallback(
    (newBeaconType: BeaconType) => {
      if (beaconType === newBeaconType) {
        return;
      }
      setBeaconType(newBeaconType);
      setFieldValue('typeProperties.beaconConnection', undefined);
      initBeaconMessageFields(newBeaconType);
    },
    [beaconType, setFieldValue, initBeaconMessageFields]
  );

  const onBeaconConnectionChange = useCallback(() => {
    initBeaconMessageFields(beaconType);
  }, [beaconType, initBeaconMessageFields]);

  const fields = useMemo(() => {
    if (!model.supportBeacon) {
      return [];
    }

    const builder = new FormConfig.FieldsBuilder().addSwitch({
      label: i18n.t('beaconFields.labels.beaconConfigSwitch'),
      name: 'lineConfigSwith',
      value: showBeaconConfig,
      onChange: onBeaconConfigSwitchChange
    });

    if (!showBeaconConfig) {
      return builder.build();
    }

    if (canChangeBeacon) {
      builder
        .addSelect({
          label: i18n.t('beaconFields.labels.beaconType'),
          name: 'beaconType',
          value: beaconType,
          simpleValue: true,
          options: beaconTypeOptions,
          onChange: onBeaconTypeChange
        })
        .addFormikSelect({
          label: i18n.t('beaconFields.labels.beaconConnection'),
          name: 'typeProperties.beaconConnection',
          options: beaconConnectionOptionsOfType,
          onChange: onBeaconConnectionChange,
          validate
        });
    } else {
      builder
        .addLabel({
          label: i18n.t('beaconFields.labels.beaconType'),
          value: beaconType,
          formatter: (value) => i18n.t(`beaconType.${camelCase(value)}`)
        })
        .addLabel({
          label: i18n.t('beaconFields.labels.beaconConnection'),
          value: beaconLabel
        });
    }
    builder.addSwitch({
      label: i18n.t('beaconFields.labels.frequencyCapSwitch'),
      name: 'frequencyCapSwitch',
      value: showFrequencyCapSetting,
      onChange: onFrequencyCapSwitchChange
    });

    if (showFrequencyCapSetting) {
      builder.addFormikCustom({
        label: '',
        name: 'frequencyControl',
        render: renderFrequencyControlInput
      });
    }

    const fields = builder.build();
    return [...fields, ...lineMessageFields, ...tenMaxBeaconMessageFields];
  }, [
    model.supportBeacon,
    beaconType,
    beaconLabel,
    canChangeBeacon,
    beaconTypeOptions,
    showBeaconConfig,
    lineMessageFields,
    tenMaxBeaconMessageFields,
    showFrequencyCapSetting,
    beaconConnectionOptionsOfType,
    validate,
    onBeaconTypeChange,
    onBeaconConnectionChange,
    onBeaconConfigSwitchChange,
    onFrequencyCapSwitchChange,
    renderFrequencyControlInput
  ]);

  return fields;
};
